import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import PaymentBadges from "@/components/PaymentBadges";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, clear } = useCart();

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-[1000px] mx-auto px-5 md:px-10 py-24 text-center">
        <h1 className="font-display text-5xl md:text-7xl mb-6">Cart's empty</h1>
        <p className="text-white/60 mb-8">Nothing added yet — take a look at the drop.</p>
        <Link to="/shop" data-testid="cart-empty-shop-cta" className="btn-primary">Browse the shop</Link>
      </div>
    );
  }

  return (
    <div data-testid="page-cart" className="max-w-[1200px] mx-auto px-5 md:px-10 py-12">
      <h1 className="font-display text-4xl md:text-6xl mb-2">Your Cart</h1>
      <p className="text-white/60 mb-10">Review your items, then head to checkout to pay via PayID.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
        <div className="md:col-span-2 space-y-6">
          {items.map((it) => (
            <div key={it.id} data-testid={`cart-item-${it.id}`} className="flex gap-4 border border-white/10 bg-[#141414] p-4">
              <img src={it.image_url} alt={it.title} className="w-28 h-32 object-cover" />
              <div className="flex-1 flex flex-col">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-display text-lg tracking-wider">{it.title}</p>
                    <p className="text-[#FF4500] font-semibold mt-1">${it.price.toFixed(2)}</p>
                  </div>
                  <button data-testid={`cart-remove-${it.id}`} onClick={() => removeItem(it.id)} className="text-white/60 hover:text-[#FF4500]">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                <div className="mt-auto flex items-center gap-3">
                  <button data-testid={`cart-decr-${it.id}`} onClick={() => updateQty(it.id, it.qty - 1)} className="w-8 h-8 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span data-testid={`cart-qty-${it.id}`} className="w-8 text-center">{it.qty}</span>
                  <button data-testid={`cart-incr-${it.id}`} onClick={() => updateQty(it.id, it.qty + 1)} className="w-8 h-8 border border-white/20 flex items-center justify-center hover:bg-white hover:text-black">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          <button data-testid="cart-clear" onClick={clear} className="text-sm text-white/50 hover:text-[#FF4500] uppercase tracking-widest">Clear cart</button>
        </div>
        <div className="border border-white/10 bg-[#141414] p-8 h-fit">
          <h3 className="font-display text-2xl mb-6 tracking-wider">Summary</h3>
          <div className="flex justify-between mb-3 text-white/70">
            <span>Subtotal</span>
            <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between mb-6 text-white/50 text-sm">
            <span>Shipping</span>
            <span>Calculated at checkout</span>
          </div>
          <div className="flex justify-between border-t border-white/10 pt-4 mb-8">
            <span className="font-display text-xl">Subtotal</span>
            <span data-testid="cart-total" className="font-display text-xl text-[#FF4500]">${subtotal.toFixed(2)}</span>
          </div>
          <Link
            to="/checkout"
            data-testid="cart-checkout"
            className="btn-primary w-full justify-center mb-3"
          >
            Proceed to checkout <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-xs text-white/50 leading-relaxed">Pay by PayID bank transfer — no card fees. Cam will confirm your order once payment clears.</p>
        </div>
      </div>
      <div className="mt-10">
        <PaymentBadges />
      </div>
    </div>
  );
}
